import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  HeartPulse,
  Sprout,
  Flame,
  Dna,
  CalendarCheck,
  TrendingUp,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import {
  useGetCoachDismissals,
  useDismissCoachSignal,
  useRestoreCoachSignal,
  getGetCoachDismissalsQueryKey,
  type CoachDismissals,
  type IntelligenceSignal,
} from "@workspace/api-client-react";
import { useIntelligenceSignals } from "./index";

/**
 * Coach AI consolidation layer.
 *
 * The Coach AI surface (Plate Talk) is the single place every cross-feature
 * signal is surfaced. This module is the pure glue that turns the flat shared
 * intelligence signals into the coach experience: an area grouping, a synthesized
 * briefing (top actions), a needs-attention count for the entry points, and a
 * persistent per-signal dismiss state. All other screens only ever show a
 * "Talk to your coach" prompt that reads {@link useCoachSignals}.
 */

export type CoachAreaKey =
  | "recovery"
  | "gut"
  | "metabolic"
  | "dna"
  | "consistency"
  | "progress"
  | "general";

/** Display order of areas in the grouped feed (most urgent first). */
export const AREA_ORDER: CoachAreaKey[] = [
  "recovery",
  "gut",
  "metabolic",
  "dna",
  "consistency",
  "progress",
  "general",
];

export const AREA_META: Record<CoachAreaKey, { icon: LucideIcon; accent: string }> =
  {
    recovery: { icon: HeartPulse, accent: "text-rose-400" },
    gut: { icon: Sprout, accent: "text-emerald-400" },
    metabolic: { icon: Flame, accent: "text-amber-400" },
    dna: { icon: Dna, accent: "text-violet-400" },
    consistency: { icon: CalendarCheck, accent: "text-cyan-400" },
    progress: { icon: TrendingUp, accent: "text-blue-400" },
    general: { icon: Sparkles, accent: "text-primary" },
  };

const GUT_SLUGS = new Set([
  "dietary_fiber",
  "fiber",
  "soluble_fiber",
  "insoluble_fiber",
  "prebiotics",
  "probiotics",
]);

/** Derive the coaching area for a signal from its type and metric. Pure. */
export function signalArea(signal: IntelligenceSignal): CoachAreaKey {
  switch (signal.type) {
    case "recovery_low":
      return "recovery";
    case "dna_flag":
      return "dna";
    case "streak":
      return "consistency";
    case "nrs_movement":
    case "deep_command_gap":
      return "progress";
    case "nutrient_deficiency":
    case "nutrient_excess":
      return signal.metricSlug && GUT_SLUGS.has(signal.metricSlug)
        ? "gut"
        : "metabolic";
    default:
      return "general";
  }
}

export interface CoachAreaGroup {
  area: CoachAreaKey;
  signals: IntelligenceSignal[];
}

/**
 * Group signals by area in {@link AREA_ORDER}, dropping empty areas. Signals are
 * assumed to already arrive sorted by descending priority, so each group keeps
 * that order.
 */
export function groupSignalsByArea(
  signals: IntelligenceSignal[],
): CoachAreaGroup[] {
  const buckets = new Map<CoachAreaKey, IntelligenceSignal[]>();
  for (const s of signals) {
    const area = signalArea(s);
    const list = buckets.get(area);
    if (list) list.push(s);
    else buckets.set(area, [s]);
  }
  return AREA_ORDER.filter((area) => buckets.has(area)).map((area) => ({
    area,
    signals: buckets.get(area)!,
  }));
}

/**
 * Pick the top 1-3 actionable signals for the briefing, preferring one per area
 * for breadth, then filling by priority. Signals must already be priority-sorted.
 */
export function pickBriefingActions(
  signals: IntelligenceSignal[],
  max = 3,
): IntelligenceSignal[] {
  const withAction = signals.filter((s) => Boolean(s.action));
  const out: IntelligenceSignal[] = [];
  const seenAreas = new Set<CoachAreaKey>();
  for (const s of withAction) {
    const area = signalArea(s);
    if (seenAreas.has(area)) continue;
    seenAreas.add(area);
    out.push(s);
    if (out.length >= max) return out;
  }
  for (const s of withAction) {
    if (out.includes(s)) continue;
    out.push(s);
    if (out.length >= max) break;
  }
  return out;
}

const DISMISS_KEY = "plate-os-coach-dismissed";

/**
 * How many days a dismissal stays in effect before the nudge naturally
 * resurfaces. Mirrors the server's `DISMISS_TTL_MS` in
 * `artifacts/api-server/src/routes/coach-dismissals.ts` (7 days) — keep the two
 * in lockstep so the "Hidden for N days" copy matches when the signal returns.
 */
export const DISMISS_TTL_DAYS = 7;

const DISMISSALS_QUERY_KEY = getGetCoachDismissalsQueryKey();

/**
 * Read the locally-cached dismissed ids. Kept as an offline fallback so a
 * dismiss feels instant and survives a reload before the server round-trips.
 * The server is the source of truth; this is only a hint while it loads.
 */
function readLocalDismissed(): string[] {
  try {
    const saved = localStorage.getItem(DISMISS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed?.ids)) return parsed.ids as string[];
    }
  } catch {
    // ignore malformed/blocked storage
  }
  return [];
}

function persistLocalDismissed(ids: Set<string>): void {
  try {
    localStorage.setItem(
      DISMISS_KEY,
      JSON.stringify({ ids: Array.from(ids) }),
    );
  } catch {
    // ignore blocked storage
  }
}

/**
 * Per-signal dismiss state, persisted server-side (per user + signal id with a
 * TTL) so a dismissal stays dismissed on every device.
 *
 * The server is the single source of truth. Two layers sit on top of it purely
 * for responsiveness, and neither is allowed to override the server:
 *  - `localCache`: the last server state, read from localStorage *once* so a
 *    reload hides items instantly while the fetch is in flight. It is ignored
 *    the moment the server responds, so an expired dismissal (no longer
 *    returned by the server) reappears even if a stale id lingers in storage.
 *  - `inFlight`: ids dismissed this session whose POST hasn't confirmed yet.
 *    Added on dismiss, cleared on success (the id is now in the server set) or
 *    rolled back on error (so a failed, non-persisted dismiss doesn't stick).
 */
export function useCoachDismissed() {
  const queryClient = useQueryClient();
  const { data } = useGetCoachDismissals({
    query: {
      queryKey: DISMISSALS_QUERY_KEY,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  });
  const { mutate } = useDismissCoachSignal();
  const { mutate: restoreMutate } = useRestoreCoachSignal();

  const serverIds = data?.signalIds;

  // Instant-feedback fallback, used only until the server responds.
  const [localCache] = useState<string[]>(() => readLocalDismissed());

  // Ids dismissed this session whose POST hasn't been confirmed yet.
  const [inFlight, setInFlight] = useState<Set<string>>(() => new Set());

  // Ids being undone this session whose DELETE hasn't confirmed yet. These
  // override every other source so an undo feels instant and the signal returns
  // to its priority position immediately.
  const [restoring, setRestoring] = useState<Set<string>>(() => new Set());

  // Keep localStorage in lockstep with the authoritative server state so the
  // next reload's instant fallback can't resurrect an expired dismissal.
  useEffect(() => {
    if (!serverIds) return;
    persistLocalDismissed(new Set(serverIds));
  }, [serverIds]);

  const dismissed = useMemo(() => {
    const next = new Set<string>();
    // Server is authoritative once loaded; only fall back to the local cache
    // while the request is still pending.
    if (serverIds) for (const id of serverIds) next.add(id);
    else for (const id of localCache) next.add(id);
    for (const id of inFlight) next.add(id);
    // An in-flight undo wins over every other source so the signal reappears
    // instantly, even before the DELETE round-trips.
    for (const id of restoring) next.delete(id);
    return next;
  }, [serverIds, localCache, inFlight, restoring]);

  const clearInFlight = useCallback((id: string) => {
    setInFlight((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const clearRestoring = useCallback((id: string) => {
    setRestoring((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const dismiss = useCallback(
    (id: string) => {
      setInFlight((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      mutate(
        { data: { signalId: id } },
        {
          onSuccess: (res: CoachDismissals) => {
            queryClient.setQueryData(DISMISSALS_QUERY_KEY, res);
            clearInFlight(id);
          },
          onError: () => {
            // Roll back the optimistic hide — the dismiss didn't persist.
            clearInFlight(id);
            void queryClient.invalidateQueries({
              queryKey: DISMISSALS_QUERY_KEY,
            });
          },
        },
      );
    },
    [mutate, queryClient, clearInFlight],
  );

  const undismiss = useCallback(
    (id: string) => {
      // Drop any pending dismiss for this id so the two don't race, and mark it
      // as restoring so it reappears in its priority slot immediately.
      clearInFlight(id);
      setRestoring((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      restoreMutate(
        { signalId: id },
        {
          onSuccess: (res: CoachDismissals) => {
            queryClient.setQueryData(DISMISSALS_QUERY_KEY, res);
            clearRestoring(id);
          },
          onError: () => {
            // Roll back the optimistic restore — the undo didn't persist, so let
            // the server state stand.
            clearRestoring(id);
            void queryClient.invalidateQueries({
              queryKey: DISMISSALS_QUERY_KEY,
            });
          },
        },
      );
    },
    [restoreMutate, queryClient, clearInFlight, clearRestoring],
  );

  return { dismissed, dismiss, undismiss };
}

export interface CoachSignalsView {
  /** All non-dismissed signals, priority-sorted. */
  visible: IntelligenceSignal[];
  /** Non-dismissed signals grouped by area. */
  grouped: CoachAreaGroup[];
  /** Top 1-3 actionable signals for the briefing. */
  briefingActions: IntelligenceSignal[];
  /** Non-dismissed signals that ask for action (excludes pure "success"). */
  needsAttentionCount: number;
  /** Total non-dismissed signals. */
  total: number;
  /**
   * Currently-dismissed nudges that are still active (their dismissal hasn't
   * expired) and still exist in the live intelligence feed. These are the live
   * signals whose id is in the server's dismissed set — the join the "Restore
   * dismissed" view needs so a user can un-hide anything they hid. Priority-sorted
   * (they inherit the feed's order). A dismissed signal whose condition has since
   * resolved (no longer in the live feed) is intentionally excluded — there's
   * nothing meaningful to restore.
   */
  dismissedSignals: IntelligenceSignal[];
  dismiss: (id: string) => void;
  /** Undo a dismissal so the signal returns to its priority position. */
  undismiss: (id: string) => void;
}

/**
 * Single hook every coach surface reads. Reads the shared intelligence signals,
 * removes dismissed ones, and derives the grouped feed + briefing + indicator.
 */
export function useCoachSignals(): CoachSignalsView {
  const all = useIntelligenceSignals();
  const { dismissed, dismiss, undismiss } = useCoachDismissed();

  return useMemo(() => {
    const visible = all.filter((s) => !dismissed.has(s.id));
    const dismissedSignals = all.filter((s) => dismissed.has(s.id));
    return {
      visible,
      grouped: groupSignalsByArea(visible),
      briefingActions: pickBriefingActions(visible),
      needsAttentionCount: visible.filter((s) => s.severity !== "success")
        .length,
      total: visible.length,
      dismissedSignals,
      dismiss,
      undismiss,
    };
  }, [all, dismissed, dismiss, undismiss]);
}
